import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useLang from '../hooks/useLang';
import { supabase } from "../supabaseClient";
import bcrypt from "bcryptjs";
import hiIcon from "../images/hi.webp";
import loginHiIcon from "../images/loginhi.webp";
import parolIcon from "../images/parol.webp";
import proflogIcon from "../images/proflog.webp";
import kodpodIcon from "../images/kodpod.webp";
import logoQaraa from "../images/logo-qaraa.webp";

/* ── Visual Tokens ── */
const s = {
  root: {
    height: "100vh",
    background: "#FAFAF8",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "32px 16px",
    position: "relative",
    overflow: "hidden",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
  },
  orb: (top, right, bottom, left, color, size) => ({
    position: "absolute", width: size, height: size, borderRadius: "50%",
    background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
    top, right, bottom, left, pointerEvents: "none",
  }),
  frame: {
    width: "100%", maxWidth: 400, maxHeight: "calc(100vh - 64px)",
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 20, position: "relative", zIndex: 2, overflowY: "auto", overflowX: "hidden",
    scrollbarWidth: "none", msOverflowStyle: "none",
  },
  seg: {
    display: "flex", background: "#F4F3EF", borderRadius: 100,
    padding: 3, width: 240, gap: 2,
  },
  segBtn: (active) => ({
    flex: 1, padding: "9px 0", fontSize: 13, fontWeight: active ? 500 : 400,
    color: active ? "#111" : "#9A9890", border: active ? "0.5px solid #E8E6DF" : "none",
    background: active ? "#fff" : "transparent", borderRadius: 100, cursor: "pointer",
    letterSpacing: "-0.1px", transition: "all .2s", fontFamily: "inherit",
  }),
  card: {
    width: "100%", background: "#fff", border: "0.5px solid #EAEAE4",
    borderRadius: 28, overflowY: "auto", flexShrink: 0,
  },
  hero: { padding: "28px 24px 16px", textAlign: "center" },
  iconWrap: {
    width: 52, height: 52, borderRadius: 16,
    background: "linear-gradient(145deg,#F7F6F2,#EEECEA)",
    border: "0.5px solid #E4E2DB", display: "flex", alignItems: "center",
    justifyContent: "center", margin: "0 auto 16px",
  },
  heroTitle: { fontSize: 22, fontWeight: 600, color: "#111", letterSpacing: "-0.5px", marginBottom: 4 },
  heroSub: { fontSize: 13, color: "#9A9890", lineHeight: 1.5, letterSpacing: "-0.1px" },
  fields: { padding: "0 22px" },
  fGroup: { marginBottom: 12 },
  fLabel: {
    fontSize: 10, fontWeight: 600, color: "#B0ADA5",
    letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6,
  },
  fBox: (active) => ({
    display: "flex", alignItems: "center",
    background: active ? "#fff" : "#F7F6F2",
    border: active ? "0.5px solid #C4B8F8" : "0.5px solid #EAEAE4",
    borderRadius: 13, padding: "0 14px", height: 48, gap: 10, transition: "all .15s",
  }),
  fInput: {
    flex: 1, border: "none", background: "transparent",
    fontSize: 14.5, color: "#111", outline: "none", fontFamily: "inherit", letterSpacing: "-0.1px",
    width: "100%",
  },
  actions: { padding: "8px 22px 28px" },
  btnPrimary: (disabled) => ({
    width: "100%", height: 50, background: disabled ? "#F5F5F5" : "#fff",
    color: disabled ? "#C7C7CC" : "#111", border: disabled ? "1.5px solid #E5E5EA" : "1.5px solid #111",
    borderRadius: 100, fontSize: 15, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    letterSpacing: "-0.2px", fontFamily: "inherit", marginBottom: 10,
    opacity: disabled ? 0.6 : 1, transition: "all .2s",
  }),
  linkRow: { textAlign: "center", marginTop: 14, fontSize: 13, color: "#9A9890" },
  errBox: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 16px", background: "#FDF4F4", 
    border: "0.5px solid rgba(220,38,38,0.12)",
    borderRadius: 14, fontSize: 13, marginBottom: 12, color: "#B91C1C", fontWeight: 500,
    lineHeight: 1.45,
  },
  errIcon: {
    flexShrink: 0, width: 28, height: 28, borderRadius: 9,
    background: "rgba(220,38,38,0.08)", display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: 13,
  },
  successBox: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 16px", background: "#F7F6F2", 
    border: "0.5px solid #EAEAE4",
    borderRadius: 14, fontSize: 13, marginBottom: 12, color: "#111", fontWeight: 500,
    lineHeight: 1.45,
  },
  successIcon: {
    flexShrink: 0, width: 28, height: 28, borderRadius: 9,
    background: "#fff", display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: 13, border: "0.5px solid #EAEAE4"
  },
  backBtn: {
    width: "100%", padding: 10, background: "transparent", border: "none",
    color: "#9A9890", fontSize: 13, fontWeight: 500, borderRadius: 10,
    cursor: "pointer", fontFamily: "inherit",
  },
  nameGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 },
  strengthBar: (active, color) => ({
    flex: 1, height: 2.5, borderRadius: 10,
    background: active ? color : "#EAEAE4", transition: "background .3s",
  }),
  genderBtn: (selected) => ({
    flex: 1, padding: 12, background: selected ? "#E8F4FD" : "#F7F6F2",
    color: selected ? "#007AFF" : "#111", fontSize: 13, fontWeight: 500,
    borderRadius: 10, cursor: "pointer", border: selected ? "0.5px solid #007AFF" : "0.5px solid #EAEAE4",
    transition: "all .2s", fontFamily: "inherit",
  }),
  selectWrap: (active) => ({
    display: "flex", alignItems: "center",
    background: active ? "#fff" : "#F7F6F2",
    border: active ? "0.5px solid #C4B8F8" : "0.5px solid #EAEAE4",
    borderRadius: 13, padding: "0 14px", height: 48, transition: "all .15s",
  }),
  selectEl: {
    flex: 1, border: "none", background: "transparent",
    fontSize: 14.5, color: "#111", outline: "none", fontFamily: "inherit",
    appearance: "none", cursor: "pointer",
  },
  progressDot: (active) => ({
    flex: 1, height: 3, borderRadius: 10,
    background: active ? "#111" : "#EAEAE4", transition: "background .3s",
  }),
  footer: { textAlign: "center", marginTop: 18, fontSize: 11.5, color: "#C8C6BF", lineHeight: 1.7 },
};

/* ── Helpers ── */
function PasswordStrength({ password }) {
  const { t } = useLang();
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password) && /[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const color = ["", "#E24B4A", "#F59E0B", "#F59E0B", "#10B981"][score] || "#EAEAE4";
  const hint = ["", t.authStrengthWeak, t.authStrengthMedium, t.authStrengthGood, t.authStrengthStrong][score] || "";
  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginTop: 7 }}>
        {[1,2,3,4].map(i => <div key={i} style={s.strengthBar(i <= score, color)} />)}
      </div>
      {hint && <div style={{ fontSize: 11, color, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function FieldBox({ label, type = "text", placeholder, value, onChange, icon, rightIcon, active, disabled }) {
  return (
    <div style={s.fGroup}>
      <div style={s.fLabel}>{label}</div>
      <div style={s.fBox(active)}>
        {icon && <span style={{ color: active ? "#A89CF7" : "#C8C6BF", fontSize: 16 }}>{icon}</span>}
        <input type={type} placeholder={placeholder} value={value} onChange={onChange}
          style={s.fInput} disabled={disabled} />
        {rightIcon}
      </div>
    </div>
  );
}

/* ── Eye Icon SVG ── */
function EyeIcon({ visible }) {
  // Hidden: eyes look left | Visible: eyes look straight
  const pupilLeftX = visible ? 16 : 11;
  const pupilRightX = visible ? 38 : 33;
  return (
    <svg width="24" height="14" viewBox="0 0 50 28" fill="none" style={{ display: "block" }}>
      <ellipse cx="14" cy="14" rx="12" ry="12" fill="#FBFBFB" stroke="#E4E2DB" strokeWidth="0.5" />
      <circle cx={pupilLeftX} cy="14" r="4" fill="#414141" />
      <ellipse cx="36" cy="14" rx="12" ry="12" fill="#FBFBFB" stroke="#E4E2DB" strokeWidth="0.5" />
      <circle cx={pupilRightX} cy="14" r="4" fill="#414141" />
    </svg>
  );
}

const formatPhone = (v) => {
  let d = v.replace(/\D/g, "");
  if (d.length > 0 && d[0] !== "7") d = "7" + d.slice(1);
  d = d.slice(0, 10);
  if (!d.length) return "";
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.slice(0, 3) + "-(" + d.slice(3);
  if (d.length <= 8) return d.slice(0, 3) + "-(" + d.slice(3, 6) + ")-" + d.slice(6);
  return d.slice(0, 3) + "-(" + d.slice(3, 6) + ")-" + d.slice(6, 8) + "-" + d.slice(8);
};
const cleanPhone = (f) => "7" + f.replace(/\D/g, "");
const formatName = (v) => {
  const l = v.replace(/[^a-zA-Zа-яА-ЯёЁ]/g, "");
  return l.length ? l[0].toUpperCase() + l.slice(1).toLowerCase() : "";
};
const formatDate = (v) => {
  const d = v.replace(/\D/g, "");
  if (d.length <= 2) return d;
  if (d.length <= 4) return d.slice(0, 2) + "." + d.slice(2);
  return d.slice(0, 2) + "." + d.slice(2, 4) + "." + d.slice(4, 8);
};
const validateDate = (ds) => {
  if (!ds || ds.replace(/\D/g, "").length !== 8) return { valid: false, error: "" };
  const m = ds.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m) return { valid: false, error: "authErrInvalidFormat" };
  const [, dd, mm, yy] = m;
  const dn = +dd, mn = +mm, yn = +yy;
  if (dn < 1 || dn > 31) return { valid: false, error: "authErrInvalidDay" };
  if (mn < 1 || mn > 12) return { valid: false, error: "authErrInvalidMonth" };
  if (yn < 1960) return { valid: false, error: "authErrMinYear" };
  const dt = new Date(yn, mn - 1, dn);
  if (dt > new Date()) return { valid: false, error: "authErrFutureDate" };
  if (dt.getDate() !== dn) return { valid: false, error: "authErrInvalidDate" };
  return { valid: true, error: "" };
};

const CITIES = [
  "Алматы","Астана","Шымкент","Актобе","Караганда","Тараз","Павлодар",
  "Усть-Каменогорск","Семей","Атырау","Костанай","Кызылорда","Уральск",
  "Петропавловск","Актау","Темиртау","Туркестан","Кокшетау","Талдыкорган",
  "Экибастуз","Рудный","Жанаозен","Балхаш","Сатпаев","Жезказган",
  "Кентау","Капшагай","Степногорск","Аксай","Атбасар",
];

/* ── Main Component ── */
export default function AuthScreens({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLang();
  const [tab, setTab] = useState(searchParams.get("mode") === "register" ? "reg" : "login"); // "login" | "reg"

  // Steps: 1=phone, 1.5=password(login), 2=code(reg), 3=profile(reg)
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [loginPwd, setLoginPwd] = useState("");
  const [code, setCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(300);
  const [resendTimer, setResendTimer] = useState(60);
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", birthDate: "", gender: "", city: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dateErr, setDateErr] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showForgotPwd, setShowForgotPwd] = useState(false);

  // Forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotCode, setForgotCode] = useState("");
  const [forgotVerified, setForgotVerified] = useState(false);
  const [forgotErr, setForgotErr] = useState("");
  const [forgotNewP, setForgotNewP] = useState("");
  const [forgotNewP2, setForgotNewP2] = useState("");

  const WA = "77768883007";

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Timer
  useEffect(() => {
    if (step === 2) {
      const t = setInterval(() => {
        setTimeLeft((p) => (p > 0 ? p - 1 : 0));
        setResendTimer((p) => (p > 0 ? p - 1 : 0));
      }, 1000);
      return () => clearInterval(t);
    }
  }, [step]);

  useEffect(() => {
    if (timeLeft === 0 && step === 2) setError(t.authErrCodeExpired);
  }, [timeLeft, step]);

  // Auto-hide errors and notices
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), step === 2 ? 2000 : 5000);
    return () => clearTimeout(t);
  }, [error, step]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(""), 3000);
    return () => clearTimeout(t);
  }, [notice]);


  // Prefetch
  const prefRef = React.useRef(null);
  useEffect(() => {
    const c = phone.replace(/\D/g, "");
    if (c.length >= 10) {
      const db = c.length === 10 ? "7" + c : c;
      if (prefRef.current?.phone === db) return;
      prefRef.current = { phone: db, promise: supabase.from("customers").select("*").eq("phone", db).maybeSingle() };
    }
  }, [phone]);

  // Switch tab
  const switchTab = (t) => {
    setTab(t); setPhone(""); setLoginPwd(""); setCode(""); setStep(1);
    setError(""); setCustomer(null); setNotice("");
  };

  /* ── Phone Submit ── */
  const handlePhone = async (e) => {
    e.preventDefault(); setError(""); setNotice("");
    const cp = cleanPhone(phone);
    if (cp.length < 11 || !cp.startsWith("77")) { setError(t.authErrCorrectPhone); return; }
    setLoading(true);
    try {
      let cust, err;
      if (prefRef.current?.phone === cp) { const r = await prefRef.current.promise; cust = r.data; err = r.error; }
      else { const r = await supabase.from("customers").select("*").eq("phone", cp).maybeSingle(); cust = r.data; err = r.error; }
      if (err) throw err;
      if (cust) {
        if (tab === "reg") { setError(t.authErrAlreadyRegistered); }
        else { setCustomer(cust); setStep(1.5); }
      } else {
        if (tab === "reg") {
          try {
            const res = await fetch("http://localhost:43123/api/send-code", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: cp }) });
            const d = await res.json();
            if (!d.success) { setError(d.message || t.authErrCodeSendFailed); setLoading(false); return; }
          } catch { setError(t.authErrBotUnavailable); setLoading(false); return; }
          setStep(2); setTimeLeft(300); setResendTimer(60);
        } else { setError(t.authErrAccountNotFound); }
      }
    } catch (e) { setError(t.authErrGeneric + ": " + (e.message || "")); }
    finally { setLoading(false); }
  };

  /* ── Password Login ── */
  const pwdRefreshRef = React.useRef(null);
  useEffect(() => {
    if (step === 1.5 && customer?.phone) {
      pwdRefreshRef.current = supabase.from("customers").select("*").eq("phone", customer.phone).maybeSingle();
    }
  }, [step, customer]);

  const handlePwdLogin = async (e) => {
    e.preventDefault(); setError(""); setNotice("");
    if (!loginPwd) return;
    setLoading(true);
    try {
      let fresh, fe;
      if (pwdRefreshRef.current) { const r = await pwdRefreshRef.current; fresh = r.data; fe = r.error; }
      else { const r = await supabase.from("customers").select("*").eq("phone", customer.phone).maybeSingle(); fresh = r.data; fe = r.error; }
      if (fe || !fresh) { setError(t.authErrProfileFetch); return; }
      if (fresh.is_banned) { setError(t.authErrBanned); return; }
      const ok = await bcrypt.compare(loginPwd, fresh.password);
      if (ok) { onLogin(fresh); navigate("/dashboard"); }
      else { setError(t.authErrWrongPassword); setLoginPwd(""); }
    } catch (e) { setError(t.authErrLoginFailed); }
    finally { setLoading(false); }
  };

  /* ── Code Verify ── */
  const handleCodeVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 4) return;
    setError(""); setLoading(true);
    try {
      const cp = cleanPhone(phone);
      const { data: c, error: ce } = await supabase.from("customers").select("*")
        .eq("phone", cp).eq("verification_code", code).eq("code_used", false)
        .gt("code_expires_at", new Date().toISOString()).maybeSingle();
      if (ce) throw ce;
      if (!c) { setError(t.authErrWrongCode); return; }
      await supabase.from("customers").update({ code_used: true }).eq("phone", cp);
      if (c.password) { onLogin(c); navigate("/dashboard"); }
      else { setStep(3); }
    } catch (e) { setError(t.authErrGeneric + ": " + (e.message || "")); }
    finally { setLoading(false); }
  };

  // Auto-verify code
  useEffect(() => { if (code.length === 4 && step === 2) handleCodeVerify({ preventDefault: () => {} }); }, [code, step]);

  /* ── Resend ── */
  const handleResend = async () => {
    setLoading(true); setError(""); setCode("");
    try {
      const cp = cleanPhone(phone);
      const r = await fetch("http://localhost:43123/api/resend-code", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: cp }) });
      const d = await r.json();
      if (d.success) { setTimeLeft(300); setResendTimer(60); setNotice(t.authNoticeCodeSent); }
      else setError(d.message || t.authErrSendFailed);
    } catch { setError(t.authErrResendBot); }
    finally { setLoading(false); }
  };

  /* ── Registration ── */
  const handleReg = async (e) => {
    e.preventDefault(); setError("");
    if (!form.firstName.trim() || form.firstName.trim().length < 2) { setError(t.authErrFirstNameMin); return; }
    if (!form.lastName.trim() || form.lastName.trim().length < 2) { setError(t.authErrLastNameMin); return; }
    const dv = validateDate(form.birthDate);
    if (!dv.valid) { setError(dv.error ? (t[dv.error] || dv.error) : t.authErrInvalidDate); return; }
    if (!form.gender) { setError(t.authErrSelectGender); return; }
    if (!form.city) { setError(t.authErrSelectCity); return; }
    if (!form.password || form.password.length < 6) { setError(t.authErrPasswordMin); return; }
    setLoading(true);
    try {
      const cp = cleanPhone(phone);
      const hp = await bcrypt.hash(form.password, 10);
      const { data: nc, error: re } = await supabase.from("customers").update({
        fullname: `${form.firstName.trim()} ${form.lastName.trim()}`,
        first_name: form.firstName.trim(), last_name: form.lastName.trim(),
        birth_date: form.birthDate, gender: form.gender, city: form.city, password: hp,
      }).eq("phone", cp).select().single();
      if (re) throw re;
      onLogin(nc); navigate("/dashboard");
    } catch (e) { setError(t.authErrGeneric + ": " + (e.message || "")); }
    finally { setLoading(false); }
  };

  /* ── Forgot password ── */
  const closeForgot = () => { setForgotOpen(false); setForgotStep(1); setForgotPhone(""); setForgotCode(""); setForgotVerified(false); setForgotErr(""); setForgotNewP(""); setForgotNewP2(""); };

  useEffect(() => {
    if (forgotStep !== 2 || forgotCode.length !== 4) return;
    (async () => {
      setForgotErr("");
      try {
        const cp = cleanPhone(forgotPhone);
        const { data: c, error } = await supabase.from("customers").select("*")
          .eq("phone", cp).eq("verification_code", forgotCode).eq("code_used", false)
          .gt("code_expires_at", new Date().toISOString()).maybeSingle();
        if (error) throw error;
        if (!c) { setForgotErr(t.authErrWrongCodeShort); return; }
        setForgotVerified(true); setForgotStep(3);
      } catch { setForgotErr(t.authErrCodeVerify); }
    })();
  }, [forgotCode, forgotStep]);

  const submitNewPass = async () => {
    setForgotErr("");
    if (!forgotVerified) { setForgotErr(t.authErrConfirmCode); return; }
    if (!forgotNewP || forgotNewP.length < 6) { setForgotErr(t.authErrMinChars); return; }
    if (forgotNewP !== forgotNewP2) { setForgotErr(t.authErrPasswordsMismatch); return; }
    try {
      const cp = cleanPhone(forgotPhone);
      const r = await fetch("http://localhost:43123/api/reset/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: cp, code: forgotCode, newPassword: forgotNewP }) });
      const d = await r.json();
      if (!r.ok || !d.success) { setForgotErr(d.message || t.authErrResetFailed); return; }
      closeForgot(); setNotice(t.authNoticePasswordUpdated);
    } catch { setForgotErr(t.authErrTechnical); }
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  /* ── RENDER ── */
  return (
    <div style={s.root}>
      <style>{`
        div[style*="overflowY: auto"]::-webkit-scrollbar {
          display: none;
        }
        @media (max-width: 480px) {
          .auth-frame input {
            padding-left: 18px !important;
          }
        }
      `}</style>
      <div style={s.orb(null, null, "-80px", "-60px", "rgba(16,185,129,.06)", "260px")} />
      <div style={s.orb("-120px", "-80px", null, null, "rgba(139,92,246,.08)", "340px")} />
      <div style={s.orb(null, "20px", "40px", null, "rgba(139,92,246,.04)", "180px")} />

      <div style={s.frame} className="auth-frame">
        {/* Logo */}
        <div style={{ cursor: "pointer", textAlign: "center" }} onClick={() => navigate("/")}>
          <img src={logoQaraa} alt="qaraa" style={{ height: 140, objectFit: "contain" }} />
          <div style={{ fontSize: 13, color: "#9A9890", marginTop: -40, letterSpacing: "-0.1px" }}>{t.authWithLove}</div>
        </div>

        {/* Segment */}
        <div style={s.seg}>
          {["login", "reg"].map((id, i) => (
            <button key={id} style={s.segBtn(tab === id)} onClick={() => switchTab(id)}>
              {i === 0 ? t.authTabLogin : t.authTabRegister}
            </button>
          ))}
        </div>

        {/* Card */}
        <div style={s.card}>
          {/* Progress dots for registration - hidden, step tracked internally */}

          {/* ── LOGIN: Step 1 — Phone ── */}
          {tab === "login" && step === 1 && (
            <>
              <div style={s.hero}>
                <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><img src={loginHiIcon} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} /></div>
                <div style={s.heroTitle}>{t.authLoginTitle}</div>
                <div style={s.heroSub}>{t.authLoginSubtitle}</div>
              </div>
              <div style={s.fields}>
                <form onSubmit={handlePhone}>
                  <div style={s.fGroup}>
                    <div style={s.fLabel}>{t.authPhoneLabel}</div>
                    <div style={s.fBox(!!phone)}>
                      <span style={{ fontSize: 14.5, color: "#111", fontWeight: 500, paddingRight: 8, borderRight: "0.5px solid #DDDBD4", marginRight: 4 }}>+7</span>
                      <input type="tel" placeholder={t.authPhonePlaceholder} value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))} style={s.fInput} disabled={loading} />
                    </div>
                  </div>
                  {error && <div style={s.errBox}><div style={s.errIcon}>⚠</div><div>{error}</div></div>}
                  {notice && <div style={s.successBox}><div style={s.successIcon}>✓</div><div>{notice}</div></div>}
                </form>
              </div>
              <div style={s.actions}>
                <button style={s.btnPrimary(loading || phone.replace(/\D/g, "").length !== 10)}
                  disabled={loading || phone.replace(/\D/g, "").length !== 10}
                  onClick={handlePhone}>
                  {loading ? t.authLoading : t.authContinue}
                </button>
                <div style={{ 
                  textAlign: "center", fontSize: 11, color: "#B0ADA5", 
                  lineHeight: 1.55, marginTop: 14, padding: "0 8px" 
                }}>
                  {t.authTermsAgreeStart}{" "}
                  <span style={{ color: "#007AFF", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/terms")}>{t.authTerms}</span>
                  {" "}{t.authAnd}{" "}
                  <span style={{ color: "#007AFF", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/privacy")}>{t.authPrivacy}</span>
                </div>
                <div style={s.linkRow}>
                  {t.authNoAccount}{" "}
                  <span onClick={() => switchTab("reg")} style={{ color: "#111", fontWeight: 500, cursor: "pointer", textDecoration: "underline" }}>
                    {t.authRegisterLink}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ── LOGIN: Step 1.5 — Password ── */}
          {tab === "login" && step === 1.5 && (
            <>
              <div style={s.hero}>
                <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><img src={parolIcon} alt="" style={{ width: 50, height: 50, objectFit: "contain" }} /></div>
                <div style={s.heroTitle}>{t.authPasswordTitle}</div>
                <div style={s.heroSub}>{t.authPasswordSubtitle}{cleanPhone(phone)}</div>
              </div>
              <div style={s.fields}>
                <div style={s.fGroup}>
                  <div style={s.fLabel}>{t.authPasswordLabel}</div>
                  <div style={s.fBox(!!loginPwd)}>
                    <input type={showPwd ? "text" : "password"} placeholder={t.authPasswordPlaceholder}
                      value={loginPwd} onChange={(e) => setLoginPwd(e.target.value)} style={s.fInput}
                      autoFocus disabled={loading}
                      onKeyDown={(e) => { if (e.key === "Enter" && loginPwd) handlePwdLogin(e); }} />
                    <span onClick={() => setShowPwd(!showPwd)} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      <EyeIcon visible={showPwd} />
                    </span>
                  </div>
                </div>
                {error && <div style={s.errBox}><div style={s.errIcon}>⚠</div><div>{error}</div></div>}
              </div>
              <div style={s.actions}>
                <button style={s.btnPrimary(loading || !loginPwd)} disabled={loading || !loginPwd} onClick={handlePwdLogin}>
                  {loading ? t.authLoggingIn : t.authLoginBtn}
                </button>
                <button style={s.backBtn} onClick={() => { setStep(1); setLoginPwd(""); setCustomer(null); setError(""); }}>
                  {t.authChangeNumber}
                </button>
                <button onClick={() => { closeForgot(); setForgotOpen(true); }} style={{
                  width: "100%", padding: "10px 0", background: "transparent", border: "none",
                  color: "#007AFF", fontSize: 13, fontWeight: 500, cursor: "pointer",
                  fontFamily: "inherit", borderRadius: 10, transition: "all .2s",
                }}>
                  {t.authForgotPassword}
                </button>
              </div>
            </>
          )}

          {/* ── REG: Step 1 — Phone ── */}
          {tab === "reg" && step === 1 && (
            <>
              <div style={s.hero}>
                <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><img src={hiIcon} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} /></div>
                <div style={s.heroTitle}>{t.authCreateTitle}</div>
                <div style={s.heroSub}>{t.authCreateSubtitle}</div>
              </div>
              <div style={s.fields}>
                <form onSubmit={handlePhone}>
                  <div style={s.fGroup}>
                    <div style={s.fLabel}>{t.authPhoneLabel}</div>
                    <div style={s.fBox(!!phone)}>
                      <span style={{ fontSize: 14.5, color: "#111", fontWeight: 500, paddingRight: 8, borderRight: "0.5px solid #DDDBD4", marginRight: 4 }}>+7</span>
                      <input type="tel" placeholder={t.authPhonePlaceholder} value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))} style={s.fInput} disabled={loading} />
                    </div>
                  </div>
                  {error && <div style={s.errBox}><div style={s.errIcon}>⚠</div><div>{error}</div></div>}
                </form>
              </div>
              <div style={s.actions}>
                <button style={s.btnPrimary(loading || phone.replace(/\D/g, "").length !== 10)}
                  disabled={loading || phone.replace(/\D/g, "").length !== 10}
                  onClick={handlePhone}>
                  {loading ? t.authLoading : t.authContinue}
                </button>
                <div style={{ 
                  textAlign: "center", fontSize: 11, color: "#B0ADA5", 
                  lineHeight: 1.55, marginTop: 14, padding: "0 8px" 
                }}>
                  {t.authTermsAgreeStart}{" "}
                  <span style={{ color: "#007AFF", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/terms")}>{t.authTerms}</span>
                  {" "}{t.authAnd}{" "}
                  <span style={{ color: "#007AFF", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/privacy")}>{t.authPrivacy}</span>
                </div>
                <div style={s.linkRow}>
                  {t.authHasAccount}{" "}
                  <span onClick={() => switchTab("login")} style={{ color: "#111", fontWeight: 500, cursor: "pointer", textDecoration: "underline" }}>
                    {t.authLoginLink}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ── REG: Step 2 — Code ── */}
          {tab === "reg" && step === 2 && (
            <>
              <div style={s.hero}>
                <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><img src={kodpodIcon} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} /></div>
                <div style={s.heroTitle}>{t.authCodeTitle}</div>
                <div style={s.heroSub}>{t.authCodeSubtitle}</div>
              </div>
              <div style={s.fields}>
                <div style={s.fGroup}>
                  <div style={s.fLabel}>{t.authCodeLabel}</div>
                  <div style={{ ...s.fBox(!!code), justifyContent: "center" }}>
                    <input type="tel" placeholder="0000" value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      style={{ ...s.fInput, textAlign: "center", fontSize: 24, letterSpacing: 10, fontWeight: 600 }}
                      maxLength={4} autoFocus disabled={loading} />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#9A9890", marginBottom: 8, textAlign: "center" }}>
                  {t.authCodeValid} {fmtTime(timeLeft)}
                </div>
                {notice && <div style={{ textAlign: "center", fontSize: 12, color: "#9A9890", marginBottom: 8 }}>{notice}</div>}
                {error && <div style={s.errBox}><div style={s.errIcon}>⚠</div><div>{error}</div></div>}
                {loading && <div style={{ textAlign: "center", fontSize: 12, color: "#9A9890" }}>{t.authChecking}</div>}
              </div>
              <div style={s.actions}>
                <button 
                  style={{ ...s.backBtn, opacity: resendTimer > 0 ? 0.5 : 1, cursor: resendTimer > 0 ? "default" : "pointer" }} 
                  onClick={resendTimer === 0 ? handleResend : undefined} 
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0 ? `${t.authResendWithTimer} (${fmtTime(resendTimer)})` : t.authResend}
                </button>
                <button style={{ ...s.backBtn, marginTop: 4 }} onClick={() => { setStep(1); setCode(""); setNotice(""); setError(""); }}>
                  {t.authChangeNumber}
                </button>
              </div>
            </>
          )}

          {/* ── REG: Step 3 — Profile ── */}
          {tab === "reg" && step === 3 && (
            <>
              <div style={s.hero}>
                <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><img src={proflogIcon} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} /></div>
                <div style={s.heroTitle}>{t.authProfileTitle}</div>
                <div style={s.heroSub}>{t.authProfileSubtitle}</div>
              </div>
              <div style={s.fields}>
                <form onSubmit={handleReg}>
                  <div style={s.nameGrid}>
                    <div>
                      <div style={s.fLabel}>{t.authFirstNameLabel}</div>
                      <div style={s.fBox(!!form.firstName)}>
                        <input type="text" placeholder={t.authFirstNamePlaceholder} value={form.firstName}
                          onChange={(e) => setForm((p) => ({ ...p, firstName: formatName(e.target.value) }))} style={s.fInput} disabled={loading} autoFocus />
                      </div>
                    </div>
                    <div>
                      <div style={s.fLabel}>{t.authLastNameLabel}</div>
                      <div style={s.fBox(!!form.lastName)}>
                        <input type="text" placeholder={t.authLastNamePlaceholder} value={form.lastName}
                          onChange={(e) => setForm((p) => ({ ...p, lastName: formatName(e.target.value) }))} style={s.fInput} disabled={loading} />
                      </div>
                    </div>
                  </div>
                  <div style={s.fGroup}>
                    <div style={s.fLabel}>{t.authBirthDateLabel}</div>
                    <div style={s.fBox(!!form.birthDate)}>
                      <input type="text" placeholder="01.01.2001" value={form.birthDate}
                        onChange={(e) => {
                          const f = formatDate(e.target.value);
                          setForm((p) => ({ ...p, birthDate: f }));
                          if (f.replace(/\D/g, "").length === 8) { const v = validateDate(f); setDateErr(v.valid ? "" : (v.error ? (t[v.error] || v.error) : "")); }
                          else setDateErr("");
                        }} style={s.fInput} disabled={loading} maxLength={10} />
                    </div>
                    {dateErr && <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{dateErr}</div>}
                  </div>
                  <div style={s.fGroup}>
                    <div style={s.fLabel}>{t.authGenderLabel}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => setForm((p) => ({ ...p, gender: "male" }))} style={s.genderBtn(form.gender === "male")}>{t.authGenderMale}</button>
                      <button type="button" onClick={() => setForm((p) => ({ ...p, gender: "female" }))} style={s.genderBtn(form.gender === "female")}>{t.authGenderFemale}</button>
                    </div>
                  </div>
                  <div style={s.fGroup}>
                    <div style={s.fLabel}>{t.authCityLabel}</div>
                    <div style={s.selectWrap(!!form.city)}>
                      <select value={form.city} onChange={setF("city")} style={s.selectEl} disabled={loading}>
                        <option value="" disabled>{t.authCitySelect}</option>
                        {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <span style={{ color: "#C8C6BF", fontSize: 10, pointerEvents: "none" }}>▼</span>
                    </div>
                  </div>
                  <div style={s.fGroup}>
                    <div style={s.fLabel}>{t.authPasswordRequired}</div>
                    <div style={s.fBox(!!form.password)}>
                      <input type={showRegPwd ? "text" : "password"} placeholder={t.authPasswordMin} value={form.password}
                        onChange={setF("password")} style={s.fInput} disabled={loading}
                        onKeyDown={(e) => { if (e.key === "Enter" && form.firstName.trim() && form.lastName.trim() && form.birthDate && form.gender && form.city && form.password) handleReg(e); }} />
                      <span onClick={() => setShowRegPwd(!showRegPwd)} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                        <EyeIcon visible={showRegPwd} />
                      </span>
                    </div>
                    <PasswordStrength password={form.password} />
                  </div>
                  {error && <div style={s.errBox}><div style={s.errIcon}>⚠</div><div>{error}</div></div>}
                </form>
              </div>
              <div style={{ ...s.actions, display: "flex", gap: 8, paddingTop: 4 }}>
                <button style={{ ...s.backBtn, flex: 1, background: "#F7F6F2", borderRadius: 13, marginBottom: 0 }}
                  onClick={() => { setStep(2); setForm({ firstName: "", lastName: "", birthDate: "", gender: "", city: "", password: "" }); setError(""); setDateErr(""); }}>
                  {t.authBack}
                </button>
                <button style={{ ...s.btnPrimary(loading || !form.firstName.trim() || !form.lastName.trim() || !form.birthDate || !form.gender || !form.city || !form.password), flex: 2, marginBottom: 0 }}
                  disabled={loading || !form.firstName.trim() || !form.lastName.trim() || !form.birthDate || !form.gender || !form.city || !form.password}
                  onClick={handleReg}>
                  {loading ? t.authRegistering : t.authCreateAccount}
                </button>
              </div>
              <div style={{ ...s.footer, paddingBottom: 20 }}>
                {t.authRegTermsStart}{" "}
                <span style={{ color: "#9A9890", textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate("/terms")}>{t.authRegTerms}</span>
                {" "}{t.authAnd}{" "}
                <span style={{ color: "#9A9890", textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate("/privacy")}>{t.authRegPrivacy}</span>
              </div>
            </>
          )}
        </div>

        <div style={s.footer}>{t.footerRights}</div>
      </div>

      {/* Forgot Password Modal */}
      {forgotOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 200 }} onClick={closeForgot}>
          <div onClick={(e) => e.stopPropagation()} style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "100%", maxWidth: 380, background: "#fff", borderRadius: 20,
            padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
          }}>
            {forgotStep === 1 && (
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: "#111", marginBottom: 4 }}>{t.authForgotTitle}</h3>
                <p style={{ fontSize: 12.5, color: "#9A9890", marginBottom: 18 }}>{t.authForgotSubtitle}</p>
                <div style={s.fLabel}>{t.authPhoneLabel}</div>
                <div style={{ ...s.fBox(!!forgotPhone), marginBottom: 14 }}>
                  <span style={{ fontSize: 14.5, color: "#111", fontWeight: 500, paddingRight: 8, borderRight: "0.5px solid #DDDBD4", marginRight: 4 }}>+7</span>
                  <input type="tel" placeholder={t.authPhonePlaceholder} value={forgotPhone}
                    onChange={(e) => { setForgotPhone(formatPhone(e.target.value)); setForgotCode(""); setForgotVerified(false); setForgotErr(""); }} style={s.fInput} />
                </div>
                <button style={s.btnPrimary(forgotPhone.replace(/\D/g, "").length !== 10)}
                  disabled={forgotPhone.replace(/\D/g, "").length !== 10}
                  onClick={() => { setForgotStep(2); setForgotCode(""); setForgotErr(""); }}>{t.authContinue}</button>
                <button style={s.backBtn} onClick={closeForgot}>{t.authCancel}</button>
              </div>
            )}
            {forgotStep === 2 && (
              <div>
                <div style={{ textAlign: "center", marginBottom: 12, fontSize: 28 }}>🔐</div>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: "#111", marginBottom: 6 }}>{t.authResetTitle}</h3>
                <p style={{ fontSize: 12.5, color: "#9A9890", lineHeight: 1.5, marginBottom: 16 }}>
                  {t.authResetDescription}
                </p>
                <a href={`https://wa.me/${WA}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-block", padding: "8px 18px", background: "#111", color: "#fff", borderRadius: 10, textDecoration: "none", fontSize: 13, fontWeight: 500, marginBottom: 16 }}>
                  {t.authOpenBot}
                </a>
                <div style={s.fLabel}>{t.authCodeVerifyLabel}</div>
                <div style={{ ...s.fBox(!!forgotCode), justifyContent: "center" }}>
                  <input type="tel" value={forgotCode} onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="0000" style={{ ...s.fInput, textAlign: "center", fontSize: 20, letterSpacing: 8, fontWeight: 600 }} />
                </div>
                {forgotErr && <div style={{ marginTop: 6, color: "#DC2626", fontSize: 12, fontWeight: 500 }}>{forgotErr}</div>}
                <button style={s.backBtn} onClick={closeForgot}>{t.authClose}</button>
              </div>
            )}
            {forgotStep === 3 && (
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 600, color: "#111", marginBottom: 12 }}>{t.authNewPasswordTitle}</h3>
                <div style={{ marginBottom: 10 }}>
                  <div style={s.fLabel}>{t.authPasswordLabel}</div>
                  <div style={s.fBox(!!forgotNewP)}>
                    <input type={showForgotPwd ? "text" : "password"} placeholder={t.authPasswordMin} value={forgotNewP}
                      onChange={(e) => setForgotNewP(e.target.value)} style={s.fInput} />
                    <span onClick={() => setShowForgotPwd(!showForgotPwd)} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      <EyeIcon visible={showForgotPwd} />
                    </span>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={s.fLabel}>{t.authConfirmPassword}</div>
                  <div style={s.fBox(!!forgotNewP2)}>
                    <input type={showForgotPwd ? "text" : "password"} placeholder={t.authRepeatPassword} value={forgotNewP2}
                      onChange={(e) => setForgotNewP2(e.target.value)} style={s.fInput} />
                    <span onClick={() => setShowForgotPwd(!showForgotPwd)} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      <EyeIcon visible={showForgotPwd} />
                    </span>
                  </div>
                </div>
                {forgotErr && <div style={{ marginBottom: 8, color: "#DC2626", fontSize: 12, fontWeight: 500 }}>{forgotErr}</div>}
                <button style={s.btnPrimary(!(forgotNewP && forgotNewP.length >= 6 && forgotNewP2))}
                  disabled={!(forgotNewP && forgotNewP.length >= 6 && forgotNewP2)}
                  onClick={submitNewPass}>{t.authSave}</button>
                <button style={s.backBtn} onClick={closeForgot}>{t.authCancel}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}